import { Box, Container, Typography } from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import { OrgAppRolesProvider } from 'src/hooks/useOrgAppRoles';
import useOrgAssociateCenters from 'src/hooks/useOrgAssociates';
import { addOrgUser, updateOrgUser } from 'src/services/apiService';
import { NewOrgUser } from 'src/types';
import AddOrgUserForm from '../AddOrgUserForm/AddOrgUserForm';
import GoBackButton from '../GoBackButton/GoBackButton';
import Loader from '../Loader/Loader';
import { rolesToPayload } from '../OrgUserRoles/utils';
import { useAddUserStyles } from './styles';

const AddUserPage: React.FC = () => {
  const history = useHistory();
  const classes = useAddUserStyles();
  const { associateId } = useParams();
  const { associateCenters, isLoading: isAssociateCenterLoading } =
    useOrgAssociateCenters();

  const { user, getCurrentOrg } = useAuth();
  const { organisationId, id } = user;

  const currentOrganisation = getCurrentOrg();
  const centerType = currentOrganisation?.centerType;

  const orgName = associateId
    ? associateCenters?.find((center) => associateId === center.bpid)?.name
    : user.organisationName;

  const orgId = useMemo(
    () => (associateId ? associateId : organisationId),
    [associateId, organisationId]
  );

  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigateToUserList = useCallback(() => {
    history.push('/organisation?tab=users');
  }, [history]);

  const handleSubmit = useCallback(
    async (
      data: NewOrgUser,
      callback: () => void,
      redirect = false,
      existingAdminsData?: { UID: string; roles: string[] }[]
    ) => {
      setIsLoading(true);
      try {
        if (existingAdminsData?.length > 0) {
          await Promise.all(
            existingAdminsData.map(async (admin) => {
              await updateOrgUser(orgId, admin.UID, {
                status: 'active',
                roles: rolesToPayload(admin.roles),
              });
            })
          );
          addNotification({
            title: 'Previous SSH Coordinator roles were updated.',
            timeout: 8000,
          });
        }

        // Add user to the current organization with roles
        await addOrgUser(orgId, data);
        addNotification({
          title: `Invitation email sent to ${data.firstName} ${data.lastName}.`,
        });

        if (typeof callback === 'function') {
          callback();
        }

        if (redirect) {
          navigateToUserList();
        }
      } catch (e) {
        const userAlreadyExistsMessage = 'User with this email already exists';
        const errorMessage = e.response?.data?.failed?.[0]?.error || '';

        addNotification({
          title:
            `Failed to send invitation email to ${data.firstName} ${data.lastName}. ${errorMessage}`.trim(),
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
        sessionStorage.removeItem('orgUsers');
      }
    },
    [
      addNotification,
      navigateToUserList,
      orgId,
      centerType,
      associateCenters,
      associateId,
    ]
  );

  useEffect(() => {
    if (centerType === 'parent' && !associateCenters?.length) {
    }
  }, [centerType, associateCenters]);

  if (isLoading || isAssociateCenterLoading) {
    return <Loader />;
  }

  return (
    <Container disableGutters maxWidth="xl">
      <GoBackButton />
      <Box mt={3}>
        <Typography variant="h2" data-testid="page-title">
          Add new user
        </Typography>
      </Box>
      <Box mt={2}>
        <Typography
          variant="body1"
          className={classes.fix}
          data-testid="page-body-text"
        >
          Create one or more new users for your organisation{' '}
          <Box component="span" className={classes.emphasizedText}>
            {orgName}
          </Box>
          .
          <br />
          <br />
          Fields marked with a{' '}
          <Box component="span" className={classes.dangerText}>
            *
          </Box>{' '}
          are required.
        </Typography>
      </Box>
      <OrgAppRolesProvider>
        <AddOrgUserForm
          onCancel={navigateToUserList}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </OrgAppRolesProvider>
    </Container>
  );
};

export default memo(AddUserPage);
